import type React from "react";
import { useState, useEffect, useRef } from "react";

interface EquirectangularImageProps {
  src: string;
  alt?: string;
  className?: string;
  onLoad?: () => void;
  onError?: () => void;
}

/**
 * Component to display a 360° equirectangular image in flat (2D) view
 * Uses a smooth transition: keeps the previous image visible while loading the new one
 */
const EquirectangularImage: React.FC<EquirectangularImageProps> = ({
  src,
  alt = "360° scene",
  className = "",
  onLoad,
  onError,
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  // Track if we have ever successfully loaded an image (to avoid flash on initial load)
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  // Keep track of the currently displayed src to detect changes
  const currentSrcRef = useRef(src);

  // Reset error state when src changes, but keep loading subtle
  useEffect(() => {
    if (src !== currentSrcRef.current) {
      currentSrcRef.current = src;
      setIsLoading(true);
      setHasError(false);
      // Don't reset hasLoadedOnce - we want to keep showing the old image
    }
  }, [src]);

  const handleLoad = () => {
    setIsLoading(false);
    setHasLoadedOnce(true);
    onLoad?.();
  };

  const handleError = () => {
    setIsLoading(false);
    setHasError(true);
    onError?.();
  };

  // Show loading spinner only on initial load (before any image is shown)
  // Once an image has been displayed, we keep it visible during transitions
  const showLoadingSpinner = isLoading && !hasLoadedOnce;

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Loading state - only show on initial load */}
      {showLoadingSpinner && (
        <div className="absolute inset-0 flex items-center justify-center bg-gray-100 z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600" />
        </div>
      )}

      {/* Error state */}
      {hasError && (
        <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-100 z-10">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-12 w-12 text-gray-400 mb-2"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
            />
          </svg>
          <p className="text-gray-500 text-sm">Image non disponible</p>
        </div>
      )}

      {/* Image - stays visible during loading if we've loaded once before */}
      <img
        src={src}
        alt={alt}
        className={`w-full h-full object-cover transition-opacity duration-200 ${
          hasError ? "opacity-0" : "opacity-100"
        }`}
        onLoad={handleLoad}
        onError={handleError}
        crossOrigin="anonymous"
      />
    </div>
  );
};

export default EquirectangularImage;
